import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getUserMailboxInfo } from 'owa-client-ids';
import popoutAddCalendarPane from 'owa-popout/lib/utils/popout/popoutAddCalendarPane';
import type EventScope from 'owa-service/lib/contract/EventScope';
import * as React from 'react';
import { lazyPopoutCalendarReadingPane } from 'owa-popout-calendar';
import { launchCalendarFullCompose } from 'owa-calendar-forms-launch-handlers';
import type { ReadingPaneSection } from 'owa-calendar-helpers-types/lib/ReadingPaneSection';
import type { FullComposeEntrySourceType } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';

/**
 * This allows an external consumer outside of the scope of Project Moca or any common code to
 * define custom callback behavior for when actions are taken on the Calendar asset. The asset
 * will use the provided callbacks, or a reasonable alternative if left empty. For example, when
 * opening a full calendar item, a consumer can specify their own custom Modal to display the data- if none
 * is given, the asset can open the item popout in a new window.
 */
export interface CalendarAssetCallbacks {
    onOpenCalendarItem: (itemId: string, scrollToSection?: ReadingPaneSection) => void;
    onOpenCalendarItemDraft: (itemId: string) => void;
    onOpenFullCompose: (event: Partial<CalendarEvent>) => void;
    onOpenEdit: (
        event: Partial<CalendarEvent>,
        eventHasUpdates: boolean,
        eventScope: EventScope
    ) => void;
    onReviewProposedNewTime: (itemId: string, newEvent?: Partial<CalendarEvent>) => void;
    onOpenAddCalendar: () => void;
}

const CalendarAssetCallbacksContext = React.createContext<CalendarAssetCallbacks>({
    onOpenCalendarItem: defaultOnOpenCalendarItem,
    onOpenCalendarItemDraft: defaultOnOpenCalendarItemDraft,
    onOpenFullCompose: defaultOnOpenFullCompose,
    onOpenEdit: defaultOnOpenEdit,
    onReviewProposedNewTime: defaultOnReviewProposedNewTime,
    onOpenAddCalendar: defaultOnOpenAddCalendar,
});
CalendarAssetCallbacksContext.displayName = 'CalendarAssetCallbacksContext';

export const CalendarAssetCallbacksProvider = CalendarAssetCallbacksContext.Provider;

export function useCalendarAssetCallbacks() {
    return React.useContext(CalendarAssetCallbacksContext);
}

const BoardCalendarAsset = 'BoardCalendarAsset';

function defaultOnOpenCalendarItem(itemId: string, scrollToSection?: ReadingPaneSection) {
    lazyPopoutCalendarReadingPane.importAndExecute(
        {
            Id: itemId,
            mailboxInfo: getUserMailboxInfo(),
        },
        BoardCalendarAsset
    );
}

function defaultOnOpenCalendarItemDraft(itemId: string) {
    launchCalendarFullCompose({
        fullComposeEntrySource: BoardCalendarAsset as FullComposeEntrySourceType,
        eventUpdates: {
            ItemId: {
                Id: itemId,
                mailboxInfo: getUserMailboxInfo(),
            },
        },
    });
}

function defaultOnOpenFullCompose(event: Partial<CalendarEvent>) {
    const itemId = event.ItemId?.Id
        ? {
              Id: event.ItemId?.Id,
              mailboxInfo: getUserMailboxInfo(),
          }
        : undefined;

    launchCalendarFullCompose({
        fullComposeEntrySource: BoardCalendarAsset as FullComposeEntrySourceType,
        eventUpdates: {
            ...event,
            ItemId: itemId,
        },
        itemHasUpdates: !!itemId,
    });
}

function defaultOnOpenEdit(
    event: Partial<CalendarEvent>,
    eventHasUpdates: boolean,
    eventScope: EventScope
) {
    launchCalendarFullCompose({
        fullComposeEntrySource: BoardCalendarAsset as FullComposeEntrySourceType,
        eventScope: eventScope,
        eventUpdates: event,
        itemHasUpdates: eventHasUpdates,
    });
}

function defaultOnReviewProposedNewTime(itemId: string, newEvent?: Partial<CalendarEvent>) {
    lazyPopoutCalendarReadingPane.importAndExecute(
        {
            Id: itemId,
            mailboxInfo: getUserMailboxInfo(),
        },
        BoardCalendarAsset
    );
}

function defaultOnOpenAddCalendar() {
    popoutAddCalendarPane(BoardCalendarAsset);
}
